import { useOutsideClick } from '@7f-ui/hooks/use-click-outside'
import { useMediaState } from '@7f-ui/hooks/use-media-state'

import { trackSearchSubmit } from '@feat/feat-tracking/search'
import { useUrlState } from '@feat/feat-url-state/use-url-state'
import { getTodayInDefaultTimeZone } from '@utils/helpers'
import { addYears } from 'date-fns'
import type { AnimationDefinition } from 'motion/react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import type { SearchResult } from '../src/lib/use-geo-search'
import { focusDateField } from '../ui/date-range-field'
import { useSearchNavigation } from './use-search-navigation'

type FormValues = {
  location: SearchResult | null
  dates: [Date | null, Date | null] | null
}

type Panel = 'location' | 'dates' | undefined

export type ActiveField = 'location' | 'dates' | undefined

export const useSearchField = () => {
  const isMobile = useMediaState('mobile')

  const pathname = usePathname()
  const searchParams = useSearchParams()

  const { urlState: params } = useUrlState()

  const [isExpanded, setIsExpanded] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [activeField, setActiveField] = useState<Panel>('location')

  const isAnimating = useRef(false)
  const startAnimating = () => {
    isAnimating.current = true
  }
  const ref = useRef<HTMLFormElement>(null)
  const collapseSearchField = () => {
    if (isAnimating.current) {
      return
    }

    setIsExpanded(false)
  }
  useOutsideClick(ref, collapseSearchField, isExpanded)

  const {
    control,
    setFocus,
    setValue,
    handleSubmit: submit,
    formState,
    clearErrors,
    reset: formReset,
  } = useForm<FormValues>({
    reValidateMode: 'onChange',
    defaultValues: { location: null, dates: null },
  })
  const { navigateToSearch } = useSearchNavigation()

  // the date field has no react-hook-form ref, so it is focused manually once it becomes active
  useEffect(() => {
    if (activeField === 'dates') {
      focusDateField()
    }
  }, [activeField])

  // reset and close the searchfield when the user switches between mobile and desktop
  useEffect(() => {
    if (isMobile) {
      // oxlint-disable-next-line react/set-state-in-effect -- resets on breakpoint change
      setIsExpanded(false)
      setActiveField('location')
      setTabIndex(0)
    } else {
      setIsModalOpen(false)
    }
  }, [isMobile])

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- resets after a route change
    setIsNavigating(false)
    setIsExpanded(false)
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- the route change is the trigger
  }, [pathname, searchParams])

  const handleLocationChange = () => {
    setActiveField('dates')
  }

  const handleSubmit = submit(
    async ({ location, dates }) => {
      if (!location) return

      trackSearchSubmit(location.query, location.name, dates)

      setIsNavigating(true)

      navigateToSearch({
        location: location.name,
        locationCoordinates: [location.lng, location.lat],
        locationType: location.placeType,
        polygon: location.polygon,
        from: dates?.[0] ?? null,
        to: dates?.[1] ?? null,
      })
    },
    ({ location, dates }) => {
      // for the location field we use react-hook-form to focus the field
      if (location) return

      // as we are not passing a ref to the date range field we need to focus it manually
      if (dates) focusDateField()
    }
  )

  const handleReset = (field: ActiveField) => {
    if (field === 'location') {
      setValue('location', null)
      setActiveField('location')
      setFocus('location')
    } else if (field === 'dates') {
      setValue('dates', [null, null])
    }
  }

  const initFormData = () => {
    setValue('dates', [params.from ?? null, params.to ?? null])
  }

  const handleExpandedComplete = (e: AnimationDefinition) => {
    if (e === 'visible') {
      isAnimating.current = false

      setTabIndex(-1)
      setActiveField('location')
      setFocus('location')
    }
  }

  const handleCollapsedComplete = (e: AnimationDefinition) => {
    if (e === 'visible') {
      // If a new expand started before collapse finished, skip resetting.
      if (isAnimating.current) return

      isAnimating.current = false
      setTabIndex(0)
      setActiveField('location')
      formReset()
    }
  }

  const min = getTodayInDefaultTimeZone()
  const max = addYears(min, 1)

  return {
    ref,
    min,
    max,
    tabIndex,
    isAnimating,
    startAnimating,
    isNavigating,
    isMobile,
    isModalOpen,
    setIsModalOpen,
    isExpanded,
    setIsExpanded,
    collapseSearchField,
    activeField,
    setActiveField,
    control,
    formState,
    formReset,
    clearErrors,
    initFormData,
    handleSubmit,
    handleLocationChange,
    handleReset,
    handleExpandedComplete,
    handleCollapsedComplete,
  }
}
